// Which states' RCRAInfo 8700-12 form offers each notification section. Used to
// hide options in the facility RCRA profile for states that don't collect them.
// States that don't file through RCRAInfo are intentionally absent.

const STATES_WITH_HSM = new Set([
  "AK",
  "AL",
  "AR",
  "AS",
  "AZ",
  "DC",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KY",
  "LA",
  "MI",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NJ",
  "NM",
  "NV",
  "OH",
  "OK",
  "PA",
  "PR",
  "SC",
  "SD",
  "TN",
  "UT",
  "VA",
  "VI",
  "WA",
  "WI",
  "WV",
  "WY",
]);

const STATES_WITH_SUBPART_K = new Set([
  "AK",
  "AL",
  "AR",
  "AS",
  "AZ",
  "CA",
  "DC",
  "FL",
  "GA",
  "GU",
  "IA",
  "ID",
  "IL",
  "IN",
  "KY",
  "MA",
  "MI",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NJ",
  "NM",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "SC",
  "SD",
  "TN",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WY",
]);

// FORM RENDERING ONLY, and read the header above for what "offers" means here:
// states that do not file through RCRAInfo are absent regardless of what their
// paper form contains.
//
// This is NOT a record of where 40 CFR 266 subpart P is in effect, and the two
// diverge. Texas is absent here yet adopted the rule effective 2022-02-03;
// TCEQ takes the federal 8700-12 by mail and tells healthcare facilities to
// check the §11d box on it. Absence here means "we do not render this state's
// form", not "this state has no pharmaceutical program".
//
// Anything deciding regulatory treatment must read `stateAdoptedSubpartP` in
// `subpartPAdoption`, sourced from EPA's adoption table.
const STATES_WITH_SUBPART_P = new Set([
  "AK",
  "AL",
  "AS",
  "AZ",
  "CO",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KY",
  "LA",
  "MD",
  "MI",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NH",
  "NJ",
  "NM",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "SC",
  "SD",
  "TN",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
]);

// Which states' 8700-12 form collects the "State Activities" multi-select. Mirrors
// the states that define a `*_STATE_ACTIVITY_OPTIONS` set in the compliance engine
// (encamp-shared can't import that package, so the list is duplicated here — keep
// the two in sync when adding a state).
const STATES_WITH_STATE_ACTIVITIES = new Set(["AL", "CA", "MA", "ME", "OH", "SC", "WI"]);

/** Whether the state's 8700-12 form offers the Hazardous Secondary Material section. */
export const stateHasHsm = (state: string | null | undefined): boolean =>
  !!state && STATES_WITH_HSM.has(state.trim().toUpperCase());

/** Whether the state's 8700-12 form offers the Subpart K (lab cleanout) section. */
export const stateHasSubpartK = (state: string | null | undefined): boolean =>
  !!state && STATES_WITH_SUBPART_K.has(state.trim().toUpperCase());

/** Whether the state's 8700-12 form offers the Subpart P (pharma) section. */
export const stateHasSubpartP = (state: string | null | undefined): boolean =>
  !!state && STATES_WITH_SUBPART_P.has(state.trim().toUpperCase());

/** Whether the state's 8700-12 form collects the "State Activities" multi-select. */
export const stateHasStateActivities = (state: string | null | undefined): boolean =>
  !!state && STATES_WITH_STATE_ACTIVITIES.has(state.trim().toUpperCase());
